// Trace hand-off scan — the cross-agent communication detector.
//
// Launch a scanning agent over ONE open trace and have it flag every moment
// that could plausibly be communication or a hand-off with ANOTHER agent:
// sub-agent delegation (already tracked structurally, still reported so the
// two readings can be aligned), files used as inter-agent mail, content
// evidently authored by another agent, human channels relaying agent-to-agent
// traffic, shared-state conventions, and references to other agents or
// sessions. The scanner is a detector, not a judge: its candidates exist to
// seed a CONTENT-BASED JOIN against the counterpart agent's own trace, so
// every candidate carries join_hints -- exact transcript strings likely to
// appear verbatim in the other session.
//
// Unlike the trace review, which judges a newest-first window and is honest
// about what it skipped, the scan's whole point is recall: a hand-off missed
// because it sat in an unread stretch is a broken join. So the transcript is
// chunked FRONT TO BACK into windows that together cover every turn, one pass
// runs per chunk, and the merged report states exactly which turns were
// scanned when a pass fails or the chunk cap binds.
//
// Citations are machine-verified against the loaded trace exactly like the
// review's: a quote the scanner invented downgrades its candidate to uncited,
// which keeps it off the timeline.

import { runSubAgent, runWithConcurrency } from "./subAgent.js";
import { turnText, normalizeForMatch, verifyCitation, windowBudgetChars } from "./traceEvaluation.js";
import { lookupCatalogModel } from "./modelCatalog.js";

// ── vocabulary ──────────────────────────────────────────────────────
// Direction is relative to the scanned session; confidence is a recall
// ladder, not a severity: "possible" is an invitation to check, and the
// scanner is told to prefer filing one over staying silent.
export const HANDOFF_DIRECTIONS = ["outbound", "inbound", "round-trip", "unclear"];
export const HANDOFF_CONFIDENCES = ["possible", "likely", "clear"];

export function confidenceRank(confidence) {
    return HANDOFF_CONFIDENCES.indexOf(confidence);
}

// ── result cache ────────────────────────────────────────────────────
// Same shape as the review's: re-opening a trace must not re-spend a
// multi-pass agent run. The version is part of the key so a shipped prompt
// or report-shape change reads as "not scanned yet" rather than serving
// answers the current scanner would not give. v4: deterministic hint-traffic
// location (report shape gains trafficTurns/located) + recurring-hint and
// earliest-occurrence prompt guidance.
const PROMPT_VERSION = 4;
const RESULT_CACHE = new Map();
const RESULT_CACHE_MAX = 20;

export function handoffCacheKey({ trace, model }) {
    return `${trace?.id || ""}:${model || ""}:v${PROMPT_VERSION}`;
}

export function readHandoffCache(key) {
    return RESULT_CACHE.get(key) || null;
}

export function writeHandoffCache(key, report) {
    if (RESULT_CACHE.size >= RESULT_CACHE_MAX) {
        const oldest = RESULT_CACHE.keys().next().value;
        if (oldest !== undefined) RESULT_CACHE.delete(oldest);
    }
    RESULT_CACHE.set(key, report);
}

// ── transcript chunking ─────────────────────────────────────────────
// Front to back, budget per chunk, every turn in exactly one chunk. The
// review's window walks newest-first and stops when the budget binds; here
// the budget decides how many passes run, not what gets read.
const PER_TURN_CHAR_CAP = 4000;
// Cost brake: a pathological trace does not get an unbounded fan-out. At the
// smallest window (40k chars) this still covers 160 capped turns per chunk;
// anything past the cap is reported unscanned rather than silently skipped.
const MAX_SCAN_CHUNKS = 16;
// Two passes in flight. The scan is a background read of a trace the user is
// looking at; saturating the proxy to finish seconds sooner is not worth it.
const SCAN_CONCURRENCY = 2;

export function buildTranscriptChunks(trace, {
    perTurnCap = PER_TURN_CHAR_CAP,
    budget = windowBudgetChars(),
    maxChunks = MAX_SCAN_CHUNKS,
} = {}) {
    const turns = trace?.turns || [];
    const chunks = [];
    let blocks = [];
    let used = 0;
    let first = 1;
    const flush = (last) => {
        if (!blocks.length) return;
        chunks.push({
            text: blocks.join("\n\n"),
            window: { firstTurn: first, lastTurn: last, totalTurns: turns.length },
        });
        blocks = [];
        used = 0;
    };
    for (let i = 0; i < turns.length; i++) {
        const full = turnText(turns[i]);
        const clipped = full.length > perTurnCap ? `${full.slice(0, perTurnCap)}…` : full;
        const block = `── turn ${i + 1} (${turns[i]?.role || "?"}) ──\n${clipped || "(no readable content)"}`;
        // A single turn larger than the whole budget still gets a chunk of its
        // own: the alternative is a turn no pass ever reads.
        if (blocks.length && used + block.length > budget) {
            flush(i);
            first = i + 1;
            // The cap binds between chunks, never mid-chunk: turns from here on
            // are honestly unscanned instead of packed into an oversized pass.
            if (chunks.length >= maxChunks) {
                return { chunks, scannedThrough: i, totalTurns: turns.length };
            }
        }
        blocks.push(block);
        used += block.length;
    }
    flush(turns.length);
    return { chunks, scannedThrough: turns.length, totalTurns: turns.length };
}

// ── output contract ─────────────────────────────────────────────────
export const HANDOFF_REPORT_SCHEMA = {
    type: "object",
    properties: {
        summary: { type: "string", description: "1-3 sentences: where in this window cross-agent communication concentrates, if anywhere. Plain prose." },
        candidates: {
            type: "array",
            description: "Possible cross-agent communications, clearest first. 0-12 entries. MERGE repeats on the same channel into one candidate citing its clearest turns. An empty array means nothing in this window even looks like one.",
            items: {
                type: "object",
                properties: {
                    title: { type: "string", description: "Short plain description of the exchange (who and what, not a category name)." },
                    channel: { type: "string", description: "YOUR kebab-case label for the channel kind (e.g. message-file, subagent-spawn, slack-relay, shared-branch). Coin freely." },
                    definition: { type: "string", description: "One line defining the channel as you mean it." },
                    direction: { type: "string", description: "outbound | inbound | round-trip | unclear — relative to THIS session." },
                    confidence: { type: "string", description: "clear (explicitly addressed: named counterpart or unambiguous protocol) | likely (the shape strongly suggests another agent) | possible (worth checking)." },
                    counterpart: { type: "string", description: "The other agent, as precisely as the evidence names it (product, model, harness, session id). Omit when unknown." },
                    structured: { type: "boolean", description: "true when the dashboard already tracks this exchange mechanically — a delegation tool call such as Task or a spawn tool. Default false." },
                    join_hints: {
                        type: "array",
                        items: { type: "string" },
                        description: "1-6 strings copied EXACTLY from the transcript and likely to appear verbatim in the counterpart's own session: file paths, distinctive message lines, ids, subject lines, sequence markers. They are used as literal search needles, so prefer distinctive strings that RECUR wherever the channel is used (a directory path, a marker prefix, an id) — never generic words.",
                    },
                    citations: {
                        type: "array",
                        description: "1-4 verbatim quotes proving the candidate. Each quote must be COPIED EXACTLY from the transcript (≤160 chars) with its 1-based turn number. Quotes are machine-verified; a paraphrase will be rejected.",
                        items: {
                            type: "object",
                            properties: {
                                turn: { type: "number", description: "1-based turn number the quote appears in." },
                                quote: { type: "string", description: "Exact substring of that turn's text." },
                            },
                        },
                    },
                },
            },
        },
    },
    required: ["summary", "candidates"],
};

// ── prompts ─────────────────────────────────────────────────────────

export function handoffSystemPrompt() {
    return [
        "You scan ONE captured AI-agent session for an AI-observability dashboard. Your single",
        "question: where might this session be COMMUNICATING WITH, HANDING WORK TO, or RECEIVING WORK",
        "FROM another agent? Another agent is any AI actor that is not this session's own reasoning",
        "loop and not the human user: sub-agents this session spawned, a parent that spawned it, other",
        "assistants and coding agents (another Claude session, Codex, Gemini, Copilot, ChatGPT, ...),",
        "and unattended model-driven automation.",
        "",
        "You are a DETECTOR, not a judge. Nothing you flag is a finding of wrongdoing; the dashboard",
        "uses your candidates to join this session to the counterpart's own trace. A hand-off you miss",
        "is a join that never happens, so lean toward reporting: a plausible candidate a human can",
        "dismiss in one click is worth more than silence. Express doubt through the confidence rung,",
        "never by withholding.",
        "",
        "WHAT TO LOOK FOR — channel shapes, not an exhaustive list. Coin your own channel label for",
        "anything that fits none of these:",
        "  • delegation tool calls — Task/Agent/spawn/sub-agent tools, workflow or batch spawns. The",
        "    dashboard already tracks these structurally; report them anyway with structured=true so",
        "    the two readings can be aligned, and so the same convention used WITHOUT the tool call",
        "    is not the one case that goes unseen.",
        "  • message files — files written or read that look like inter-agent mail: from/to or",
        "    sender/recipient headers, sequence numbers, inbox/outbox/comms naming, names pairing two",
        "    agents (claude-to-codex), handoff or handover notes, status files addressed to a reader",
        "    that is not the human.",
        "  • another agent's voice — content READ into this session that was evidently authored by",
        "    another agent: a first-person report this session did not write, another assistant's",
        "    transcript or commit, a reply appearing in a file this session was polling.",
        "  • human channels carrying agent-to-agent traffic — Slack, email, issues, PR comments, or",
        "    commit messages that address or quote another agent rather than a person.",
        "  • shared-state conventions — a branch, tag, lease or lock file, queue, database row, or",
        "    scratch file two agents take turns writing; sockets, pipes, local servers; messaging or",
        "    send tools.",
        "  • identity and session references — another agent named or quoted, another session's id,",
        "    run id, or resume handle passed around, a prompt that pastes another agent's output.",
        "",
        "WHAT NOT TO FLAG. This organization BUILDS agent tooling: its transcripts are full of code,",
        "tests, fixtures, and docs ABOUT agent communication. Working on such code is subject matter,",
        "not communication. The test: is the session ITSELF a party to the exchange — did it write",
        "content for another agent to act on, act on content another agent wrote, or operate the",
        "channel? Editing a parser for agent messages is not a hand-off; writing a message and saving",
        "it where another agent will look is. The session's own harness mechanics are also not",
        "hand-offs on their own — its tool results, its compaction summaries, its own memory notes —",
        "unless the content shows another agent on the other end.",
        "",
        "LABEL THE CHANNEL, NOT THE TOPIC. A candidate's channel is the mechanism CARRYING the",
        "exchange in this window, never the agent the content happens to discuss. A message file that",
        "reports what a sub-agent pushed is message-file traffic whose counterpart is the file's",
        "reader — the sub-agent is subject matter, and its ids and commits belong in the join hints",
        "of THAT channel. Reserve delegation labels (subagent-spawn and kin) for windows where the",
        "delegation itself is visible: the spawning tool call, its result, or the delegated report",
        "returning. A sub-agent that completed in earlier turns and is merely referenced here is",
        "context, not a new exchange.",
        "",
        "For every candidate: label the channel (kebab-case, one-line definition), give direction",
        "relative to this session (outbound | inbound | round-trip | unclear), rate confidence",
        "(clear | likely | possible), name the counterpart as precisely as the evidence allows (omit",
        "when unknown), set structured=true when a delegation tool call already carries the exchange,",
        "and supply join_hints: 1-6 strings COPIED EXACTLY from the transcript that would most likely",
        "also appear in the counterpart's own session — file paths, distinctive message sentences,",
        "ids, subject lines, sequence markers. Join hints are used verbatim as search needles across",
        "other sessions, and the dashboard also scans THIS session for them to mark every turn the",
        "channel touches — so prefer strings that RECUR wherever the channel is used (the mailbox",
        "directory path, the sequence-marker prefix, the counterpart's id) over strings from one",
        "message. A generic word is a useless needle.",
        "",
        "MERGE, don't enumerate: a mailbox directory exchanging 25 files is ONE candidate citing the",
        "clearest few turns, not 25. Always include the channel's EARLIEST occurrence in your window",
        "among its citations — that is where a channel is established and named, and it must not",
        "depend on which later turns you happened to quote. You see a WINDOW of the session; turns",
        "outside it exist that you were not shown, so never claim a channel is unused elsewhere.",
        "",
        "Evidence rules: a candidate exists only if you can cite it. Quotes must be EXACT substrings",
        "of the transcript (they are machine-verified against the raw text; paraphrase fails). An",
        "empty candidates array is a valid answer for a window with nothing that even looks like",
        "cross-agent traffic.",
        "",
        "When done call return_result with the report. Do not narrate in prose.",
    ].join("\n");
}

function asIso(ts) {
    if (!ts) return "";
    if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? "" : ts.toISOString();
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? String(ts) : d.toISOString();
}

export function buildHandoffTask(trace, transcript, window) {
    return [
        "Scan this session window for cross-agent communication candidates.",
        "",
        "SESSION (this side of any exchange):",
        `- trace_id: ${trace?.id || ""}`,
        `- endpoint: ${trace?.endpointName || ""}`,
        `- timestamp: ${asIso(trace?.timestamp)}`,
        `- model / provider: ${trace?.model || "?"} / ${trace?.provider || "?"}`,
        `- session summary: ${trace?.summary || ""}`,
        `- window: turns ${window.firstTurn}-${window.lastTurn} of ${window.totalTurns} (turns outside this range were NOT shown to you)`,
        "",
        "TRANSCRIPT:",
        transcript || "(empty window)",
    ].join("\n");
}

// ── report normalization + citation verification ────────────────────

function slugify(s) {
    return String(s || "").trim().toLowerCase().replace(/\s+/g, "-");
}

const JOIN_HINT_CAP = 240;

function normalizeCandidate(raw, turnTexts) {
    if (!raw || typeof raw !== "object") return null;
    const citations = (Array.isArray(raw.citations) ? raw.citations : [])
        .map((c) => verifyCitation(c, turnTexts));
    const located = citations.filter((c) => c.verified !== "missing");
    const direction = HANDOFF_DIRECTIONS.includes(String(raw.direction || "").trim())
        ? String(raw.direction).trim()
        : "unclear";
    const confidence = HANDOFF_CONFIDENCES.includes(String(raw.confidence || "").trim())
        ? String(raw.confidence).trim()
        : "possible";
    const joinHints = (Array.isArray(raw.join_hints) ? raw.join_hints : [])
        .map((h) => String(h ?? "").trim())
        .filter(Boolean)
        .map((h) => (h.length > JOIN_HINT_CAP ? h.slice(0, JOIN_HINT_CAP) : h))
        .slice(0, 6);
    return {
        title: String(raw.title || "").trim() || "(untitled candidate)",
        channel: slugify(raw.channel) || "unlabelled",
        definition: String(raw.definition || "").trim() || null,
        direction,
        confidence,
        counterpart: String(raw.counterpart || "").trim() || null,
        structured: raw.structured === true,
        joinHints,
        citations,
        citedCount: located.length,
        citationCount: citations.length,
        uncited: located.length === 0,
        turns: [...new Set(located.map((c) => c.locatedTurn))].sort((a, b) => a - b),
    };
}

// ── cross-window exchange merge ─────────────────────────────────────
// Each pass reports a channel it sees independently, so an exchange that
// spans windows — the mailbox conversation that runs the whole session —
// comes back once per window, usually under drifting labels
// ("shared-file-mailbox" in one window, "message-file" in the next). The
// join wants one candidate per real channel, and a summary reading
// "6 candidates · 6 channels" for one mailbox is the drift showing through.
//
// Two candidates are the same exchange when:
//   • a join hint overlaps — equal after case-folding, or one contains the
//     other (a hinted file inside a hinted directory). Hints are exact
//     transcript strings, so overlap is the strongest same-channel signal
//     and it is immune to label drift; or
//   • they carry the same channel label AND name the same counterpart.
//     Label alone is not enough: two different sub-agent spawns are two
//     exchanges that happen to share a kind.
// Anything else stays separate — failing toward not merging keeps distinct
// exchanges distinct at the cost of a less compressed summary.
//
// Two guards outrank hint overlap, because artifacts travel through EVERY
// channel that discusses them: a session's file mailbox reporting what its
// sub-agent pushed hints the same commit sha the spawn candidate does, and
// on that overlap the first shipped merge folded the mailbox into the spawn
// — relabelling a shared-file channel "subagent-delegation" on the axis.
//   • the structured boundary: a mechanically-tracked delegation and a
//     free-form channel are different exchanges however much they talk
//     about the same commits;
//   • counterpart contradiction: when both sides NAME who they are talking
//     to and the names disagree on the agent (leading token — naming drifts
//     in the tail, "Codex (gpt-5.6)" vs "Codex session…", but leads with
//     the agent), they are two conversations, not one.
const HINT_CONTAIN_MIN = 8;

function hintMatch(a, b) {
    const x = a.toLowerCase();
    const y = b.toLowerCase();
    if (x === y) return true;
    const short = x.length <= y.length ? x : y;
    const long = x.length <= y.length ? y : x;
    return short.length >= HINT_CONTAIN_MIN && long.includes(short);
}

function counterpartLead(counterpart) {
    const m = String(counterpart || "").toLowerCase().match(/[a-z0-9]+/);
    return m ? m[0] : "";
}

function counterpartsContradict(a, b) {
    const la = counterpartLead(a.counterpart);
    const lb = counterpartLead(b.counterpart);
    return Boolean(la && lb && la !== lb);
}

function sameExchange(a, b) {
    if (a.structured !== b.structured) return false;
    if (counterpartsContradict(a, b)) return false;
    if (a.joinHints.some((ha) => b.joinHints.some((hb) => hintMatch(ha, hb)))) return true;
    if (a.channel === b.channel) {
        const ca = (a.counterpart || "").trim().toLowerCase();
        const cb = (b.counterpart || "").trim().toLowerCase();
        if (ca && cb && ca === cb) return true;
    }
    return false;
}

// Union cap above the per-candidate wire cap of 6: a merged exchange has
// genuinely more needles, but the tooltip and the join both want the best
// few, not everything every window repeated.
const MERGED_HINT_CAP = 8;

function foldGroup(members) {
    if (members.length === 1) return members[0];
    // The lead member speaks for the group: best confidence, then best
    // evidence, then earliest appearance. Its title, label, and definition
    // rode the strongest reading of the exchange.
    const lead = [...members].sort((a, b) =>
        (confidenceRank(b.confidence) - confidenceRank(a.confidence))
        || (b.citedCount - a.citedCount)
        || ((a.turns[0] ?? Infinity) - (b.turns[0] ?? Infinity)))[0];
    const dirs = new Set(members.map((m) => m.direction).filter((d) => d !== "unclear"));
    const direction = dirs.has("round-trip") || (dirs.has("outbound") && dirs.has("inbound"))
        ? "round-trip"
        : dirs.size === 1 ? [...dirs][0] : "unclear";
    const joinHints = [];
    const seenHints = new Set();
    for (const m of [lead, ...members.filter((x) => x !== lead)]) {
        for (const h of m.joinHints) {
            const k = h.toLowerCase();
            if (seenHints.has(k) || joinHints.length >= MERGED_HINT_CAP) continue;
            seenHints.add(k);
            joinHints.push(h);
        }
    }
    const citations = members.flatMap((m) => m.citations);
    const located = citations.filter((c) => c.verified !== "missing");
    return {
        ...lead,
        direction,
        // Uniform across the group: sameExchange never merges across the
        // structured boundary.
        structured: lead.structured,
        counterpart: lead.counterpart || members.map((m) => m.counterpart).find(Boolean) || null,
        joinHints,
        citations,
        citedCount: located.length,
        citationCount: citations.length,
        uncited: located.length === 0,
        turns: [...new Set(located.map((c) => c.locatedTurn))].sort((a, b) => a - b),
    };
}

export function mergeHandoffCandidates(candidates) {
    const groups = [];
    for (const c of candidates) {
        // Membership is transitive through any member, so a hint chain
        // (A↔B on one hint, B↔C on another) folds into one exchange. The
        // contradiction guard therefore has to hold against EVERY member,
        // not just the one that matched: an unnamed candidate overlapping
        // both sides would otherwise bridge two named counterparts that
        // directly contradict, re-opening the over-merge through a 3-node
        // chain.
        const g = groups.find((members) =>
            members.some((m) => sameExchange(m, c))
            && !members.some((m) => counterpartsContradict(m, c)));
        if (g) g.push(c);
        else groups.push([c]);
    }
    return groups.map(foldGroup);
}

// The turns whose text carries any of the candidate's join hints, beyond the
// ones its citations already mark. Hints below the containment floor are
// skipped for the same reason they are skipped as merge signals: a short
// string matches everywhere and would paint the whole axis.
function locateHintTraffic(candidate, turnTexts) {
    const hints = candidate.joinHints
        .map((h) => normalizeForMatch(h))
        .filter((h) => h.length >= HINT_CONTAIN_MIN);
    if (!hints.length) return [];
    const cited = new Set(candidate.turns);
    const traffic = [];
    for (let i = 0; i < turnTexts.length; i++) {
        if (cited.has(i + 1)) continue;
        if (hints.some((h) => turnTexts[i].includes(h))) traffic.push(i + 1);
    }
    return traffic;
}

// Merge the per-chunk passes into one report. Coverage is computed from the
// windows of the passes that actually returned, so a failed pass shows as a
// gap rather than silently inflating "scanned".
export function finalizeHandoffScan(passes, { trace } = {}) {
    const turnTexts = (trace?.turns || []).map((t) => normalizeForMatch(turnText(t)));
    const candidates = [];
    const scanned = [];
    let failedPasses = 0;
    for (const p of passes || []) {
        if (p?.status === "done" && p.raw && typeof p.raw === "object") {
            scanned.push([p.window.firstTurn, p.window.lastTurn]);
            for (const raw of Array.isArray(p.raw.candidates) ? p.raw.candidates : []) {
                const c = normalizeCandidate(raw, turnTexts);
                if (c) candidates.push(c);
            }
        } else {
            failedPasses++;
        }
    }
    scanned.sort((a, b) => a[0] - b[0]);
    const ranges = [];
    for (const r of scanned) {
        const prev = ranges[ranges.length - 1];
        if (prev && r[0] <= prev[1] + 1) prev[1] = Math.max(prev[1], r[1]);
        else ranges.push([...r]);
    }
    const totalTurns = trace?.turns?.length || 0;
    const scannedTurns = ranges.reduce((n, [a, b]) => n + (b - a + 1), 0);
    const merged = mergeHandoffCandidates(candidates);
    // Deterministic traffic: once the scanner has named a channel, its join
    // hints are exact transcript strings, so the turns carrying them are a
    // substring scan, not a judgment. The scanner cites a few turns per
    // exchange BY DESIGN (merge, don't enumerate) and which few varies run
    // to run; the hint scan is identical every run, so the axis stops
    // flickering. It also rescues a candidate whose quotes all failed
    // verification: paraphrased evidence loses the citation marks, not the
    // whole channel.
    for (const c of merged) {
        c.trafficTurns = locateHintTraffic(c, turnTexts);
        c.located = c.turns.length > 0 || c.trafficTurns.length > 0;
    }
    merged.sort((a, b) =>
        (confidenceRank(b.confidence) - confidenceRank(a.confidence))
        || ((a.turns[0] ?? a.trafficTurns[0] ?? Infinity) - (b.turns[0] ?? b.trafficTurns[0] ?? Infinity)));
    // Channel vocabulary from LOCATED candidates only — a candidate with no
    // verified citation and no hint traffic asserts nothing the reader can
    // navigate to, and it must not pad the summary's counts either.
    const channels = [...new Set(merged.filter((c) => c.located).map((c) => c.channel))];
    return {
        candidates: merged,
        channels,
        coverage: {
            ranges,
            scannedTurns,
            totalTurns,
            passCount: (passes || []).length,
            failedPasses,
        },
    };
}

// ── marks on the turn axis ──────────────────────────────────────────
// Same collapse-and-merge the review marks do, with confidence standing where
// severity stood: the axis shows WHERE candidates sit, the tooltip says what
// they are. 1-based report turns become 0-based axis turns HERE, so no caller
// can get the conversion wrong.
export function handoffMarks(report, turnCount) {
    const byTurn = new Map();
    for (const c of report?.candidates || []) {
        if (c.uncited) continue;
        for (const cited of c.turns) {
            const turn = cited - 1;
            if (!(turn >= 0 && turn < turnCount)) continue;
            const at = byTurn.get(turn) || { turn, candidates: [], confidence: "possible" };
            at.candidates.push(c);
            if (confidenceRank(c.confidence) > confidenceRank(at.confidence)) at.confidence = c.confidence;
            byTurn.set(turn, at);
        }
    }
    return [...byTurn.values()].sort((a, b) => a.turn - b.turn);
}

// Channel traffic on the axis: the hint-located turns, collapsed the same way
// the cited marks are. A turn any candidate CITES keeps only its glyph — a
// tick under a mark would say less than the mark already does. Shares the
// mark shape ({turn, candidates, confidence}) so mergeHandoffMarks applies.
export function handoffTrafficMarks(report, turnCount) {
    const citedTurns = new Set();
    for (const c of report?.candidates || []) {
        for (const t of c.turns) citedTurns.add(t);
    }
    const byTurn = new Map();
    for (const c of report?.candidates || []) {
        for (const t of c.trafficTurns || []) {
            if (citedTurns.has(t)) continue;
            const turn = t - 1;
            if (!(turn >= 0 && turn < turnCount)) continue;
            const at = byTurn.get(turn) || { turn, candidates: [], confidence: "possible" };
            if (!at.candidates.includes(c)) at.candidates.push(c);
            if (confidenceRank(c.confidence) > confidenceRank(at.confidence)) at.confidence = c.confidence;
            byTurn.set(turn, at);
        }
    }
    return [...byTurn.values()].sort((a, b) => a.turn - b.turn);
}

// Fold traffic marks into SPANS: a new mark chains onto the open span when
// the gap from the span's END is under gapPx. Deliberately different from
// mergeHandoffMarks, which measures from the segment's START — right for
// point glyphs, where a badge must stay near the turns it counts, but wrong
// for coverage: it chopped a channel active every other turn into a row of
// gap-sized dashes, which is the tick wall this fold exists to replace.
export function foldTrafficSpans(marks, cellW, gapPx) {
    const out = [];
    for (const mark of marks) {
        const prev = out[out.length - 1];
        if (prev && cellW > 0 && gapPx > 0 && (mark.turn - prev.through) * cellW < gapPx) {
            for (const c of mark.candidates) {
                if (!prev.candidates.includes(c)) prev.candidates.push(c);
            }
            prev.through = mark.turn;
            continue;
        }
        out.push({ turn: mark.turn, through: mark.turn, candidates: [...mark.candidates] });
    }
    return out;
}

export function mergeHandoffMarks(marks, cellW, minPx) {
    const out = [];
    for (const mark of marks) {
        const prev = out[out.length - 1];
        if (prev && cellW > 0 && minPx > 0 && (mark.turn - prev.turn) * cellW < minPx) {
            for (const c of mark.candidates) {
                if (!prev.candidates.includes(c)) prev.candidates.push(c);
            }
            if (confidenceRank(mark.confidence) > confidenceRank(prev.confidence)) prev.confidence = mark.confidence;
            prev.through = mark.turn;
            continue;
        }
        out.push({ ...mark, candidates: [...mark.candidates], through: mark.turn });
    }
    return out;
}

// The rail tooltip's channel inventory: one row per channel kind among
// LOCATED candidates, carrying how many exchanges use it and who the
// counterparts are. Rows come out in the report's own order (confidence,
// then first appearance), so the channel most worth reading leads.
export function handoffChannelRows(report) {
    const byChannel = new Map();
    for (const c of report?.candidates || []) {
        if (!handoffLocated(c)) continue;
        const at = byChannel.get(c.channel) || { channel: c.channel, count: 0, counterparts: [] };
        at.count++;
        if (c.counterpart && !at.counterparts.includes(c.counterpart)) at.counterparts.push(c.counterpart);
        byChannel.set(c.channel, at);
    }
    return [...byChannel.values()];
}

// The strip's one-line verdict. "no candidates" and "not scanned" must not
// read the same, and a partial scan says so in the same breath as its count.
// The channel count appears only when it compresses: "6 candidates · 6
// channels" tells the reader nothing the first number didn't, and reads like
// two claims when it is one.
// Whether a candidate can put anything on the axis: a verified citation or a
// hint-located turn. `located` is stamped by finalizeHandoffScan; the
// fallback keeps readers of an older cached report shape honest.
function handoffLocated(c) {
    return c.located ?? (!c.uncited || (c.trafficTurns || []).length > 0);
}

export function handoffSummaryLine(report) {
    if (!report) return "";
    const locatable = (report.candidates || []).filter(handoffLocated);
    const partial = report.coverage && report.coverage.scannedTurns < report.coverage.totalTurns;
    const scope = partial
        ? ` · scanned ${report.coverage.scannedTurns} of ${report.coverage.totalTurns} turns`
        : "";
    if (!locatable.length) return `no hand-off candidates${scope}`;
    const ch = report.channels.length;
    const channelPart = ch > 0 && ch < locatable.length ? ` · ${ch} channel${ch === 1 ? "" : "s"}` : "";
    return `${locatable.length} candidate${locatable.length === 1 ? "" : "s"}${channelPart}${scope}`;
}

// ── runner ──────────────────────────────────────────────────────────

// Run the scan over the whole trace. Resolves
// { status: "done", report } | { status: "aborted" } | { status: "error", error }.
// Partial failure is a done report with coverage gaps: one dead pass must not
// throw away the candidates every other pass found.
export async function runHandoffScan({
    trace,
    creds = null,
    model = null,
    signal,
    onProgress = null,
}) {
    if (!trace?.turns?.length) return { status: "error", error: new Error("trace has no readable turns") };
    const sys = handoffSystemPrompt();
    const probe = buildHandoffTask(trace, "", {
        firstTurn: 1, lastTurn: trace.turns.length, totalTurns: trace.turns.length,
    });
    const contextWindow = lookupCatalogModel(creds?.provider, model)?.contextWindow || null;
    const budget = windowBudgetChars({ contextWindow, prefixChars: sys.length + probe.length });
    const { chunks } = buildTranscriptChunks(trace, { budget });
    let done = 0;
    onProgress?.({ done: 0, total: chunks.length });
    const tasks = chunks.map((chunk) => async () => {
        let r;
        try {
            r = await runSubAgent({
                role: "primary",
                model: model || null,
                signal,
                tools: [],
                outputSchema: HANDOFF_REPORT_SCHEMA,
                systemPrompt: sys,
                task: buildHandoffTask(trace, chunk.text, chunk.window),
            });
        } catch (e) {
            r = { status: "error", error: e instanceof Error ? e : new Error(String(e)) };
        }
        done++;
        onProgress?.({ done, total: chunks.length });
        return { window: chunk.window, status: r.status, raw: r.status === "done" ? r.result : null, error: r.error || null };
    });
    const settled = await runWithConcurrency(tasks, SCAN_CONCURRENCY);
    const passes = settled.map((s, i) => (s.ok
        ? s.value
        : { window: chunks[i].window, status: "error", raw: null, error: s.error }));
    if (signal?.aborted || passes.some((p) => p.status === "aborted")) return { status: "aborted" };
    if (!passes.some((p) => p.status === "done" && p.raw && typeof p.raw === "object")) {
        const firstError = passes.find((p) => p.error)?.error;
        return { status: "error", error: firstError || new Error("the scan returned no report") };
    }
    return { status: "done", report: finalizeHandoffScan(passes, { trace }) };
}
